import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/widgets";
import { daysLabel, naira } from "@/lib/helpers";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wrench, FileText, Plus, Check, Droplet, Disc, Battery, Sparkles, CheckCircle2, Trash2 } from "lucide-react";

const typeIcon = { oil: Droplet, tyre: Disc, brake: Disc, battery: Battery, wash: Sparkles };

export default function Care() {
  const [tasks, setTasks] = useState([]);
  const [docs, setDocs] = useState([]);
  const [taskForm, setTaskForm] = useState({ title: "", type: "oil", due_date: "", cost: "" });
  const [docForm, setDocForm] = useState({ doc_type: "Insurance", number: "", expiry_date: "" });
  const [taskOpen, setTaskOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);

  const load = () => {
    api.get("/maintenance").then((r) => setTasks(r.data)).catch(() => {});
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    try { await api.put(`/maintenance/${id}/complete`); toast.success("Marked done. Nice one!"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delTask = async (id) => { await api.delete(`/maintenance/${id}`); load(); };
  const delDoc = async (id) => { await api.delete(`/documents/${id}`); load(); };

  const addTask = async () => {
    try {
      await api.post("/maintenance", { ...taskForm, cost: parseFloat(taskForm.cost) || 0 });
      toast.success("Reminder added.");
      setTaskOpen(false); setTaskForm({ title: "", type: "oil", due_date: "", cost: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const addDoc = async () => {
    if (!docForm.expiry_date) { toast.error("Pick an expiry date."); return; }
    try {
      await api.post("/documents", docForm);
      toast.success("Document saved.");
      setDocOpen(false); setDocForm({ doc_type: "Insurance", number: "", expiry_date: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const pending = tasks.filter((t) => !t.completed);
  const done = tasks.filter((t) => t.completed);

  return (
    <div className="space-y-6" data-testid="care-page">
      <PageHeader title="Car Care" subtitle="Service reminders & documents." icon={Wrench} />

      <Tabs defaultValue="maintenance">
        <TabsList className="grid w-full grid-cols-2 rounded-full h-12 p-1 bg-zinc-100">
          <TabsTrigger value="maintenance" data-testid="tab-maintenance" className="rounded-full data-[state=active]:bg-white">Maintenance</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="rounded-full data-[state=active]:bg-white">Documents</TabsTrigger>
        </TabsList>

        {/* Maintenance */}
        <TabsContent value="maintenance" className="mt-5 space-y-4">
          <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-task-btn" className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 gap-2"><Plus size={18} /> Add reminder</Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle>New maintenance reminder</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input data-testid="task-title" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="Engine oil change" className="mt-1.5 h-11 rounded-xl" /></div>
                <div><Label>Type</Label>
                  <select value={taskForm.type} onChange={(e) => setTaskForm({ ...taskForm, type: e.target.value })} className="mt-1.5 h-11 w-full rounded-xl border border-zinc-200 px-3 text-sm">
                    {["oil", "tyre", "brake", "battery", "wash", "other"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><Label>Due date</Label><Input data-testid="task-date" type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} className="mt-1.5 h-11 rounded-xl" /></div>
              </div>
              <DialogFooter><Button data-testid="task-save" onClick={addTask} className="rounded-full bg-emerald-500 hover:bg-emerald-600">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          {pending.length === 0 ? (
            <EmptyState icon={CheckCircle2} title="All caught up!" text="No pending maintenance. Your car dey okay." />
          ) : pending.map((t) => {
            const Icon = typeIcon[t.type] || Wrench;
            const overdue = t.due_date && new Date(t.due_date) < new Date();
            return (
              <div key={t.id} className="flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white p-4" data-testid="task-item">
                <div className={`grid h-10 w-10 place-items-center rounded-xl ${overdue ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}><Icon size={20} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-800">{t.title}</div>
                  <div className={`text-xs ${overdue ? "text-red-600" : "text-zinc-400"}`}>{t.due_date ? (overdue ? "Overdue" : `Due ${t.due_date}`) : "No date"}</div>
                </div>
                <button data-testid="task-complete" onClick={() => complete(t.id)} className="grid h-9 w-9 place-items-center rounded-full border border-zinc-200 text-zinc-400 hover:border-emerald-400 hover:text-emerald-600"><Check size={16} /></button>
                <button onClick={() => delTask(t.id)} className="text-zinc-300 hover:text-red-500"><Trash2 size={16} /></button>
              </div>
            );
          })}

          {done.length > 0 && (
            <div>
              <h3 className="mb-2 mt-6 text-sm font-semibold text-zinc-500">Completed</h3>
              <div className="space-y-2">
                {done.slice(0, 5).map((t) => (
                  <div key={t.id} className="flex items-center gap-3 rounded-2xl border border-zinc-100 bg-zinc-50 p-3 text-sm text-zinc-400">
                    <CheckCircle2 size={16} className="text-emerald-400" /><span className="line-through">{t.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents" className="mt-5 space-y-4">
          <Dialog open={docOpen} onOpenChange={setDocOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-doc-btn" className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 gap-2"><Plus size={18} /> Add document</Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle>Track a document</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Type</Label>
                  <select value={docForm.doc_type} onChange={(e) => setDocForm({ ...docForm, doc_type: e.target.value })} className="mt-1.5 h-11 w-full rounded-xl border border-zinc-200 px-3 text-sm">
                    {["Vehicle Licence", "Insurance", "Roadworthiness", "Registration"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div><Label>Number (optional)</Label><Input data-testid="doc-number" value={docForm.number} onChange={(e) => setDocForm({ ...docForm, number: e.target.value })} className="mt-1.5 h-11 rounded-xl" /></div>
                <div><Label>Expiry date</Label><Input data-testid="doc-date" type="date" value={docForm.expiry_date} onChange={(e) => setDocForm({ ...docForm, expiry_date: e.target.value })} className="mt-1.5 h-11 rounded-xl" /></div>
              </div>
              <DialogFooter><Button data-testid="doc-save" onClick={addDoc} className="rounded-full bg-emerald-500 hover:bg-emerald-600">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          {docs.length === 0 ? (
            <EmptyState icon={FileText} title="No documents yet" text="Add your licence, insurance and more to get renewal reminders." />
          ) : docs.map((d) => {
            const critical = d.days_left != null && d.days_left < 7;
            const warn = d.days_left != null && d.days_left < 30;
            return (
              <div key={d.id} className="flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white p-4" data-testid="doc-item">
                <div className={`grid h-10 w-10 place-items-center rounded-xl ${critical ? "bg-red-50 text-red-600" : warn ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}><FileText size={20} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-800">{d.doc_type}</div>
                  <div className={`text-xs ${critical ? "text-red-600" : warn ? "text-amber-600" : "text-zinc-400"}`}>{daysLabel(d.days_left)}</div>
                </div>
                {warn && <Button size="sm" data-testid="doc-renew-btn" className="rounded-full bg-zinc-900 hover:bg-zinc-800 text-xs h-8" onClick={() => toast.info("Renewal partner integration coming soon.")}>Renew Now</Button>}
                <button onClick={() => delDoc(d.id)} className="text-zinc-300 hover:text-red-500"><Trash2 size={16} /></button>
              </div>
            );
          })}
        </TabsContent>
      </Tabs>
    </div>
  );
}
